import { ChakraProvider } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import theme from '../common/theme';
import Loader from '../components/common/Loader';
import i18n, { isLocaleResourcesLoaded } from '../locale/index';

import { LoaderProvider } from '../context/LoaderContext';
import RedirectParameterProvider from '../context/RedirectParameterContext';

// Custom hook to handle i18n ready state
const useI18nReady = () => {
  const [isI18nReady, setIsI18nReady] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    let intervalId;

    const checkReady = () => {
      return i18n.isInitialized && i18n.language && isLocaleResourcesLoaded();
    };

    const setReady = () => {
      if (isMounted) {
        setIsI18nReady(true);
      }
    };

    // Check if already ready
    if (checkReady()) {
      // Add a small delay to ensure translations are fully loaded
      timeoutId = setTimeout(setReady, 100);
    } else {
      // Poll for i18n initialization and language detection
      intervalId = setInterval(() => {
        if (checkReady()) {
          clearInterval(intervalId);
          // Add a small delay to ensure translations are fully loaded
          timeoutId = setTimeout(setReady, 100);
        }
      }, 50);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  return isI18nReady;
};

export const Providers = ({ children }) => {
  const isI18nReady = useI18nReady();

  // Show loader while i18n is initializing
  if (!isI18nReady) {
    return <Loader isOpen={true} />;
  }

  return (
    <ChakraProvider theme={theme}>
      <I18nextProvider i18n={i18n}>
        <LoaderProvider>
          <RedirectParameterProvider>{children}</RedirectParameterProvider>
        </LoaderProvider>
      </I18nextProvider>
    </ChakraProvider>
  );
};
